import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import { safeReturnTo, type SsoError } from '../auth/callback';
import { SculptMark } from '../components/Layout';
import { Alert } from '../components/ui';

/** "Continue with Microsoft" only: a full-page trip through sculpt-api's /login/microsoft with client=backoffice,
 *  back to /auth/callback. The API opens /admin only to a Microsoft session from the Peerception tenant, so there is
 *  no password form. A refusal comes back as `ssoError` (AuthCallback). */
export function Login() {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const state = location.state as { from?: string; ssoError?: SsoError } | null;
  const returnTo = safeReturnTo(state?.from ?? null);
  const error = state?.ssoError ? t(`login.sso.${state.ssoError}`) : null;

  return (
    <div className="grid min-h-screen place-items-center bg-pane p-6">
      <div className="w-full max-w-sm">
        <button type="button" onClick={() => void i18n.changeLanguage(i18n.language === 'fr' ? 'en' : 'fr')} className="absolute right-4 top-3 h-7 rounded-[5px] px-2 font-mono text-[11px] text-muted hover:bg-hover-soft" title={t('header.language')}>
          {i18n.language === 'fr' ? 'EN' : 'FR'}
        </button>
        <div className="mb-6 flex items-center gap-3">
          <SculptMark size={36} />
          <div>
            <div className="text-[20px] font-medium leading-tight tracking-[-0.02em] text-ink">{t('login.title')}</div>
            <div className="text-[12.5px] text-muted">{t('login.subtitle')}</div>
          </div>
        </div>
        <a href={api.microsoftLoginUrl(returnTo)} data-testid="login-microsoft" className="flex h-9 items-center justify-center gap-2 rounded-[5px] border border-line-btn bg-white text-[12.5px] font-medium text-ink hover:bg-hover">
          <MicrosoftGlyph />
          {t('login.microsoft')}
        </a>
        {error && <div className="mt-4"><Alert>{error}</Alert></div>}
      </div>
    </div>
  );
}

function MicrosoftGlyph() {
  return (
    <svg width="14" height="14" viewBox="0 0 21 21" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}
